import Row from 'antd/es/grid/row'
import { useAppSelector } from '../../../redux/hooks.ts'
import { selectThemeStyle } from '../../../features/setting/setting.slice.ts'
import Col from 'antd/es/grid/col'
import Select from 'antd/es/select/index'
import { useDispatch } from 'react-redux'
import {
  selectManagementInput,
  setAccountStatisticsTrialType,
  setCategory,
  setManagementFirm,
  setManagementInputDateRange,
} from '../../../redux/slices/management-input.slice.ts'
import { useListFirmOptionsQuery } from '../../../apis/option/option-api.api.ts'
import IdeyaRangePicker from '../../../common/antd/ideya-rangepicker.tsx'
import { USER_CONTROL_COL } from '@/common/constants/user-controls-layout.ts'
import dayjs from 'dayjs'
import { convertPxToNumber } from '../../../utils/helpers.ts'
import Spin from 'antd/es/spin/index'
import Tooltip from 'antd/es/tooltip/index'
import { InfoCircleOutlined } from '@ant-design/icons'
import { getCategoryOptions } from '../../../utils/selector-options.ts'
import { useTranslation } from 'react-i18next'
import { selectUser } from '../../../features/user/user.slice.ts'
import { StatisticsAccountType } from '../../../enums/management-input.enum.ts'
import {
  canSeeCategorySelectPermissions,
  canSeeFirmSelectPermissions,
  hasPermission,
} from '../../../utils/permissions.ts'
import { ReloadControl } from '../../../common/components/reload-control.tsx'

interface StatisticsUserControlsProps {
  onReload: () => void
}

export const StatisticsUserControls = ({
  onReload,
}: StatisticsUserControlsProps) => {
  const { t } = useTranslation()
  const themeStyle = useAppSelector(selectThemeStyle)
  const dispatch = useDispatch()
  const managementInput = useAppSelector(selectManagementInput)
  const userInfo = useAppSelector(selectUser)

  const { data: firmOptionData, isLoading: isLoadingFirmOptions } =
    useListFirmOptionsQuery()

  if (isLoadingFirmOptions) {
    return <Spin />
  }

  const canFilterByCategory = hasPermission(
    canSeeCategorySelectPermissions({
      groupId: userInfo.groupId,
      groupRole: userInfo.groupRole,
    }),
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  const canFilterByFirm = hasPermission(
    canSeeFirmSelectPermissions,
    userInfo.firmRole,
    userInfo.groupRole,
    userInfo.role
  )

  return (
    <Row
      style={{
        marginBottom: themeStyle.marginSM,
      }}
      gutter={[
        convertPxToNumber(themeStyle.marginXS),
        convertPxToNumber(themeStyle.marginXS),
      ]}
    >
      {canFilterByCategory && (
        <Col xs={24} md={USER_CONTROL_COL.select}>
          <Row>
            <Select
              showSearch
              value={managementInput.category}
              style={{ width: '100%' }}
              onChange={(value) => dispatch(setCategory(value))}
              options={getCategoryOptions(t)}
            />
          </Row>
        </Col>
      )}
      {canFilterByFirm && (
        <Col xs={24} md={USER_CONTROL_COL.select}>
          <Row>
            <Select
              showSearch
              value={managementInput.firm}
              style={{ width: '100%' }}
              onChange={(value) => dispatch(setManagementFirm(value))}
              options={[
                {
                  value: '',
                  label: t('common.user_controls.all_firms'),
                },
                ...(firmOptionData?.data.map((firm) => ({
                  value: firm.value,
                  label: firm.label,
                })) ?? []),
              ]}
              filterOption={(input, option) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </Row>
        </Col>
      )}
      {userInfo.role !== '' && (
        <Col xs={24} md={USER_CONTROL_COL.select}>
          <Row>
            <Select
              showSearch
              value={managementInput.accountStatisticsTrialType}
              style={{ width: '100%' }}
              onChange={(value) =>
                dispatch(setAccountStatisticsTrialType(value))
              }
              options={[
                {
                  value: StatisticsAccountType.All,
                  label: t('common.user_controls.all_accounts'),
                },
                {
                  value: true,
                  label: StatisticsAccountType.Trial,
                },
                {
                  value: false,
                  label: StatisticsAccountType.NotTrial,
                },
              ]}
            />
          </Row>
        </Col>
      )}
      <Col xs={'1 1 auto'} md={USER_CONTROL_COL.dateRange}>
        <Row wrap={false}>
          <IdeyaRangePicker
            value={[
              dayjs(managementInput.dateRange[0]),
              dayjs(managementInput.dateRange[1]),
            ]}
            onChange={(dates) => {
              dispatch(
                setManagementInputDateRange([
                  dates?.[0] ? dates[0].format('YYYY-MM-DD') : null,
                  dates?.[1] ? dates[1].format('YYYY-MM-DD') : null,
                ])
              )
            }}
          />
          <Tooltip title={t('statistics.user_controls.time_interval_tooltip')}>
            <InfoCircleOutlined
              style={{
                color: '#D3A342',
                marginLeft: 8,
                cursor: 'pointer',
              }}
            />
          </Tooltip>
        </Row>
      </Col>
      <Col xs={'auto'}>
        <ReloadControl
          variant={'button'}
          throttleMs={180000}
          onReload={onReload}
        />
      </Col>
    </Row>
  )
}
